/**
 * Pure helpers for building an organization forest from flat user rows.
 * Reporting edges use `manager-id-matcher.js` (mgr_id + legacy aliases).
 */

import { normalizeId, managerIdCandidates } from '@/utils/manager-id-matcher'

/** Same leading-zero fold as manager-id-matcher (private there; needed for map lookup). */
function stripLeadingZeros(normalized) {
  if (!normalized) return normalized
  return normalized.replace(/^([A-Z]*)0*(\d+)$/, '$1$2')
}

export function displayName(user) {
  if (!user) return ''
  if (user.name && String(user.name).trim()) return String(user.name).trim()
  const first = user.first_name || user.firstName || ''
  const last = user.last_name || user.lastName || ''
  const combined = `${first} ${last}`.trim()
  if (combined) return combined
  return user.emp_id ? String(user.emp_id) : ''
}

function pickDisplayString(value) {
  if (value == null || value === '') return ''
  if (typeof value === 'string' || typeof value === 'number') {
    return String(value).trim()
  }
  if (typeof value === 'object') {
    return pickDisplayString(
      value.name
      || value.title
      || value.label
      || value.designation
      || value.value,
    )
  }
  return ''
}

/**
 * Job title / designation from org-chart user rows.
 * Primary API field: `position_title` (sign-in userData + employee lists).
 * `title` on dropdown/list rows is usually the employee display name — skipped
 * when it matches `displayName()`.
 */
export function displayDesignation(user) {
  if (!user) return ''
  const displayNameNorm = displayName(user).toLowerCase()
  const titleCandidate = pickDisplayString(user.title)
  const candidates = [
    user.designation,
    user.position_title,
    user.emp_designation,
    user.designation_name,
    user.employee_title,
    user.job_title,
    user.jobTitle,
    user.position,
    user.position_name,
    user.employee && user.employee.designation,
    user.profile && user.profile.designation,
    user.employee_info && user.employee_info.designation,
    titleCandidate && titleCandidate.toLowerCase() !== displayNameNorm
      ? titleCandidate
      : '',
    user.role,
    Array.isArray(user.roles) && user.roles.length ? user.roles[0] : '',
  ]
  for (let i = 0; i < candidates.length; i += 1) {
    const picked = pickDisplayString(candidates[i])
    if (picked) return picked
  }
  return ''
}

export function displayTitle(user) {
  return displayDesignation(user)
}

export function displayDepartment(user) {
  if (!user) return ''
  return pickDisplayString(user.department) || pickDisplayString(user.vertical)
}

export function displayVertical(user) {
  if (!user) return ''
  return String(user.vertical || '').trim()
}

export function displayAvatar(user) {
  if (!user) return null
  return user.avatar || user.profile_pic || user.photo || null
}

export function displayEmail(user) {
  if (!user) return ''
  return user.email || user.work_email || ''
}

export function displayPhone(user) {
  if (!user) return ''
  return user.contact_no || user.phone || user.mobile || user.mobile_no || ''
}

/**
 * Active-employee convention shared with Attendance team lists.
 * Rows with no status but a present emp_id are treated as active.
 */
export function isActiveUser(user) {
  if (!user) return false
  const status = user.status || user.employee_status || user.is_active
  if (status === undefined || status === null || status === '') {
    return !!user.emp_id
  }
  return (
    status === 'Active'
    || status === 'active'
    || status === true
    || status === 'ACTIVE'
    || status === 1
    || status === '1'
  )
}

export function filterUsersByActive(users, showInactive = false) {
  if (!Array.isArray(users)) return []
  if (showInactive) return users.filter(u => u && u.emp_id)
  return users.filter(u => u && u.emp_id && isActiveUser(u))
}

function sortByName(a, b) {
  return displayName(a).localeCompare(displayName(b), undefined, { sensitivity: 'base' })
}

/**
 * Build Map<normalizedEmpId, user> plus a loose (zero-stripped) secondary index.
 */
function buildUserMaps(users) {
  const byNorm = new Map()
  const byLoose = new Map()
  users.forEach(user => {
    const norm = normalizeId(user.emp_id)
    if (!norm) return
    byNorm.set(norm, user)
    const loose = stripLeadingZeros(norm)
    if (loose && !byLoose.has(loose)) byLoose.set(loose, user)
  })
  return { byNorm, byLoose }
}

function resolveManagerUser(member, byNorm, byLoose) {
  const selfNorm = normalizeId(member.emp_id)
  const candidates = managerIdCandidates(member)
  for (let i = 0; i < candidates.length; i += 1) {
    const candidate = candidates[i]
    if (!candidate || candidate === selfNorm) continue
    let manager = byNorm.get(candidate)
    if (!manager) {
      manager = byLoose.get(stripLeadingZeros(candidate))
    }
    if (manager && normalizeId(manager.emp_id) !== selfNorm) {
      return manager
    }
  }
  return null
}

/**
 * Build a forest of org nodes from flat users.
 * Each node: { user, empId, children, managerEmpId }
 *
 * Roots = users whose manager is missing, self, or not in the active set.
 * Cycles are broken by promoting the cycle edge to a root + console.warn.
 *
 * @param {object[]} users Already status-filtered (or full set)
 * @returns {{ roots: object[], nodeByEmpId: Map<string, object> }}
 */
export function buildOrgForest(users) {
  const list = Array.isArray(users) ? users.filter(u => u && normalizeId(u.emp_id)) : []
  const { byNorm, byLoose } = buildUserMaps(list)
  const nodeByEmpId = new Map()

  list.forEach(user => {
    const empIdNorm = normalizeId(user.emp_id)
    nodeByEmpId.set(empIdNorm, {
      user,
      empId: user.emp_id,
      empIdNorm,
      children: [],
      managerEmpId: null,
      managerEmpIdNorm: null,
    })
  })

  const parentOf = new Map() // childNorm -> parentNorm

  nodeByEmpId.forEach((node, empIdNorm) => {
    const manager = resolveManagerUser(node.user, byNorm, byLoose)
    if (!manager) return
    const mgrNorm = normalizeId(manager.emp_id)
    if (!mgrNorm || !nodeByEmpId.has(mgrNorm)) return

    // Cycle check: walk ancestors of manager; if we hit this node, orphan.
    let cursor = mgrNorm
    const seen = new Set([empIdNorm])
    let cycle = false
    while (cursor) {
      if (seen.has(cursor)) {
        cycle = true
        break
      }
      seen.add(cursor)
      cursor = parentOf.get(cursor) || null
    }
    if (cycle) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('[org-chart] Cycle detected; treating as root:', {
          emp_id: node.empId,
          manager_emp_id: manager.emp_id,
        })
      }
      return
    }

    node.managerEmpId = manager.emp_id
    node.managerEmpIdNorm = mgrNorm
    parentOf.set(empIdNorm, mgrNorm)
  })

  const roots = []
  nodeByEmpId.forEach((node, empIdNorm) => {
    const parentNorm = parentOf.get(empIdNorm)
    if (!parentNorm) {
      roots.push(node)
      return
    }
    const parent = nodeByEmpId.get(parentNorm)
    if (!parent) {
      roots.push(node)
      return
    }
    parent.children.push(node)
  })

  const sortTree = nodes => {
    nodes.sort((a, b) => sortByName(a.user, b.user))
    nodes.forEach(n => {
      if (n.children.length) sortTree(n.children)
    })
  }
  sortTree(roots)

  return { roots, nodeByEmpId }
}

/**
 * Return subtree rooted at empId (normalized match), or empty forest if not found.
 * nodeByEmpId contains only nodes in the subtree.
 */
export function getSubtreeForest(nodeByEmpId, rootEmpId) {
  const norm = normalizeId(rootEmpId)
  if (!norm || !nodeByEmpId.has(norm)) {
    return { roots: [], nodeByEmpId: new Map() }
  }
  const root = nodeByEmpId.get(norm)
  const subMap = new Map()
  const walk = node => {
    subMap.set(node.empIdNorm, node)
    node.children.forEach(walk)
  }
  walk(root)
  return { roots: [root], nodeByEmpId: subMap }
}

/**
 * Collect ancestor empIdNorms from node up to (and including) roots.
 */
export function collectAncestorNormIds(nodeByEmpId, empId) {
  const start = normalizeId(empId)
  if (!start || !nodeByEmpId.has(start)) return []
  const path = []
  let cursor = start
  const seen = new Set()
  while (cursor && !seen.has(cursor)) {
    seen.add(cursor)
    path.push(cursor)
    const node = nodeByEmpId.get(cursor)
    cursor = node && node.managerEmpIdNorm ? node.managerEmpIdNorm : null
  }
  return path
}

/**
 * Search forest for nodes matching name / emp_id / role / department.
 * Returns array of matching empIdNorm strings.
 */
export function searchOrgNodes(nodeByEmpId, query) {
  const q = String(query || '').trim().toLowerCase()
  if (!q) return []
  const matches = []
  nodeByEmpId.forEach((node, empIdNorm) => {
    const u = node.user
    const haystack = [
      displayName(u),
      u.emp_id,
      displayDesignation(u),
      displayDepartment(u),
    ].filter(Boolean).join(' ').toLowerCase()
    if (haystack.includes(q)) matches.push(empIdNorm)
  })
  return matches
}

/** 0 = roots only (all teams collapsed). Click expands that person's direct reports. */
export const DEFAULT_ORG_EXPAND_DEPTH = 0

/**
 * Default expand: all nodes within `depth` levels from roots (0 = only root cards).
 * Returns Set of empIdNorm that should be collapsed (children hidden).
 * Default 0 => only roots visible; every node with children starts collapsed.
 */
export function defaultCollapsedSet(roots, expandLevels = DEFAULT_ORG_EXPAND_DEPTH) {
  const collapsed = new Set()
  const walk = (nodes, depth) => {
    nodes.forEach(node => {
      if (node.children.length && depth >= expandLevels) {
        collapsed.add(node.empIdNorm)
      }
      if (node.children.length) walk(node.children, depth + 1)
    })
  }
  walk(roots, 0)
  return collapsed
}

export function allCollapsibleNormIds(roots) {
  const ids = []
  const walk = nodes => {
    nodes.forEach(node => {
      if (node.children.length) {
        ids.push(node.empIdNorm)
        walk(node.children)
      }
    })
  }
  walk(roots)
  return ids
}

/**
 * Accordion expand: open `expandedEmpIdNorm` and keep its ancestors open so the
 * path stays visible; collapse every other node that has children.
 * Opening person B therefore closes person A's team (and any sibling branches).
 */
export function exclusiveExpandCollapsedSet(roots, nodeByEmpId, expandedEmpIdNorm) {
  const collapsed = new Set(allCollapsibleNormIds(roots))
  if (!expandedEmpIdNorm) return collapsed
  const keepOpen = collectAncestorNormIds(nodeByEmpId, expandedEmpIdNorm)
  keepOpen.forEach(id => collapsed.delete(id))
  return collapsed
}

